<script setup lang="ts">
/* ===========================================================================
   ui-delta — InfoPopover
   ---------------------------------------------------------------------------
   ADDS:      A small text link with an icon that opens a light card on hover,
              on keyboard focus, or on tap — for context a reader wants now and
              then, without it taking a line of the page all the time.
   UPSTREAM:  @ui Tooltip holds one line of plain text; this holds a titled
              card with lists and paragraphs.
   AT MERGE:  Move into @ui beside Tooltip.
   ---------------------------------------------------------------------------
   Hover opens it for a mouse; a click pins it open (and is the only way on a
   touch screen); Escape or a click outside closes it.

   `labelHidden` keeps the label for assistive technology but shows only the
   icon — for an ⓘ beside a heading. The card flips to the other edge when
   the side it was asked for would run it off its bounds, and where neither
   side fits it is nudged back inside the gutter. Its bounds are the nearest
   container that would clip it (a scrolling side panel), else the window,
   and it is never wider than they leave room for.
   =========================================================================== */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'

const props = withDefaults(defineProps<{ label: string; align?: 'start' | 'end'; labelHidden?: boolean }>(), {
  align: 'start',
  labelHidden: false,
})

const root = ref<HTMLElement | null>(null)
const card = ref<HTMLElement | null>(null)
const hovered = ref(false)
const pinned = ref(false)
const flipped = ref(false)
const shift = ref(0)
const limit = ref<number | null>(null)
const id = useId()

// The page's side gutter, so a nudged card lines up with the content edge.
const GUTTER = 16

const open = computed(() => hovered.value || pinned.value)
const side = computed(() => {
  if (!flipped.value) return props.align
  return props.align === 'start' ? 'end' : 'start'
})

const measure = async (): Promise<DOMRect | null> => {
  await nextTick()
  return card.value?.getBoundingClientRect() ?? null
}

/** The left and right edges the card must stay inside: the nearest ancestor that clips, else the window. */
const boundsOf = (element: HTMLElement | null): { left: number; right: number } => {
  for (let node = element?.parentElement ?? null; node && node !== document.body; node = node.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(node)
    if (overflowX !== 'visible' || overflowY !== 'visible') {
      const box = node.getBoundingClientRect()
      return { left: Math.max(0, box.left), right: Math.min(window.innerWidth, box.right) }
    }
  }
  return { left: 0, right: window.innerWidth }
}

watch(open, async (isOpen) => {
  flipped.value = false
  shift.value = 0
  limit.value = null
  if (!isOpen) return
  const bounds = boundsOf(root.value)
  const start = bounds.left + GUTTER
  const edge = bounds.right - GUTTER
  limit.value = edge - start
  const asked = await measure()
  if (!asked) return
  flipped.value = props.align === 'start' ? asked.right > edge : asked.left < start
  const placed = flipped.value ? await measure() : asked
  if (!placed) return
  if (placed.left < start) shift.value = start - placed.left
  else if (placed.right > edge) shift.value = edge - placed.right
})

const cardStyle = computed(() => ({
  ...(shift.value ? { transform: `translateX(${shift.value}px)` } : {}),
  ...(limit.value !== null ? { maxWidth: `${limit.value}px` } : {}),
}))

const onDocument = (event: MouseEvent): void => {
  if (pinned.value && root.value && !root.value.contains(event.target as Node)) pinned.value = false
}
const onKey = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    pinned.value = false
    hovered.value = false
  }
}
onMounted(() => {
  document.addEventListener('click', onDocument)
  document.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocument)
  document.removeEventListener('keydown', onKey)
})
</script>

<template>
  <span ref="root" class="info-popover" @mouseenter="hovered = true" @mouseleave="hovered = false">
    <button
      type="button"
      class="info-popover__trigger"
      :aria-expanded="open"
      :aria-controls="id"
      @click="pinned = !pinned"
      @focus="hovered = true"
      @blur="hovered = false"
    >
      <slot name="icon" />
      <span :class="{ 'sr-only': labelHidden }">{{ label }}</span>
    </button>
    <div
      v-show="open"
      :id="id"
      ref="card"
      role="dialog"
      :aria-label="label"
      class="info-popover__card"
      :class="`is-${side}`"
      :style="cardStyle"
    >
      <slot />
    </div>
  </span>
</template>

<style scoped>
.info-popover {
  position: relative;
  display: inline-flex;
}

.info-popover__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xxs) 0;
  border: 0;
  background: transparent;
  color: var(--fg-secondary);
  font: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 0.2em;
  cursor: pointer;
}

.info-popover__trigger:hover,
.info-popover__trigger[aria-expanded='true'] {
  color: var(--fg-primary);
}

/* The card sits flush under the trigger, so the pointer can travel into it
   without crossing a gap that would close it. */
.info-popover__card {
  position: absolute;
  top: 100%;
  z-index: 20;
  width: min(26rem, calc(100vw - var(--spacing-3xl) * 2));
  padding: var(--spacing-xl);
  border: 1px solid var(--line-default);
  border-radius: var(--radius-xl);
  background: var(--surface-raised);
  box-shadow: var(--shadow-card-float);
  color: var(--fg-primary);
  font-size: var(--font-size-body-3);
}

.info-popover__card.is-start {
  left: 0;
}

.info-popover__card.is-end {
  right: 0;
}
</style>
